import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { closingNoteActions } from '../actions/closing-note.actions';
import { CommentCounts, TodoCounts } from '../components/notes-overview/notes-overview.types';
import {
    selectAccountError,
    selectAccountLoading,
    selectAccountNotes,
    selectAccountTodos,
    selectAccountTodosError,
    selectAccountTodosLoading,
    selectCommentCounts,
    selectCommentFilter,
    selectComments,
    selectCommentsError,
    selectCommentsLoading,
    selectComposerKind,
    selectDetail,
    selectDetailError,
    selectDetailLoading,
    selectDetailMissing,
    selectDocumentError,
    selectDocumentLoading,
    selectDocumentNotes,
    selectEditingNoteId,
    selectExpandedNoteIds,
    selectRecentlyChangedId,
    selectSearchTerm,
    selectSort,
    selectTodoAudience,
    selectTodoCounts,
    selectTodoStatus,
    selectTodos,
    selectTodosError,
    selectTodosLoading,
    selectWriteError,
} from '../selectors/closing-note.selectors';
import { IClosingNote, ICreateNoteRequest, IUpdateNoteRequest, NoteKindWire } from '../services/closing-note.service';

/**
 * Die eine Tuer, durch die die Hosts an die Notizen kommen — REV-21374.
 *
 * Wie die Geschwister-Provider: der Store bleibt eine Implementierungsfrage dieser Bibliothek, und
 * die Seiten sehen Observables und Methoden statt Aktionen und Selektoren.
 */
@Injectable()
export class ClosingNoteProvider {
    public constructor(private readonly store: Store, private readonly actions$: ActionsSubject) {}

    // ── Lesen ─────────────────────────────────────────────────────────────────────────────────

    public readonly comments$: Observable<IClosingNote[]> = this.store.select(selectComments);
    public readonly todos$: Observable<IClosingNote[]> = this.store.select(selectTodos);
    public readonly accountNotes$: Observable<IClosingNote[]> = this.store.select(selectAccountNotes);

    public readonly commentsLoading$ = this.store.select(selectCommentsLoading);
    public readonly commentsError$ = this.store.select(selectCommentsError);
    public readonly todosLoading$ = this.store.select(selectTodosLoading);
    public readonly todosError$ = this.store.select(selectTodosError);
    public readonly accountLoading$ = this.store.select(selectAccountLoading);
    public readonly accountError$ = this.store.select(selectAccountError);
    public readonly accountTodos$: Observable<IClosingNote[]> = this.store.select(selectAccountTodos);
    public readonly accountTodosLoading$ = this.store.select(selectAccountTodosLoading);
    public readonly accountTodosError$ = this.store.select(selectAccountTodosError);

    /** REV-21369 — die Kommentare des offenen Belegs, für den Viewer und seine Spalte. */
    public readonly documentNotes$: Observable<IClosingNote[]> = this.store.select(selectDocumentNotes);
    public readonly documentLoading$ = this.store.select(selectDocumentLoading);
    public readonly documentError$ = this.store.select(selectDocumentError);

    public readonly detail$ = this.store.select(selectDetail);
    public readonly detailLoading$ = this.store.select(selectDetailLoading);
    public readonly detailError$ = this.store.select(selectDetailError);
    public readonly detailMissing$ = this.store.select(selectDetailMissing);

    public readonly commentCounts$: Observable<CommentCounts> = this.store.select(selectCommentCounts);
    public readonly todoCounts$: Observable<TodoCounts> = this.store.select(selectTodoCounts);

    public readonly commentFilter$ = this.store.select(selectCommentFilter);
    public readonly todoStatus$ = this.store.select(selectTodoStatus);
    public readonly todoAudience$ = this.store.select(selectTodoAudience);
    public readonly searchTerm$ = this.store.select(selectSearchTerm);
    public readonly sort$ = this.store.select(selectSort);

    public readonly expandedNoteIds$ = this.store.select(selectExpandedNoteIds);
    public readonly editingNoteId$ = this.store.select(selectEditingNoteId);
    public readonly composerKind$ = this.store.select(selectComposerKind);
    public readonly recentlyChangedId$ = this.store.select(selectRecentlyChangedId);
    public readonly writeError$ = this.store.select(selectWriteError);

    /** `true` when a Pendenz was created, `false` when creating failed — for a dialog that stays open until saved. */
    public readonly createOutcome$: Observable<boolean> = this.actions$.pipe(
        ofType(closingNoteActions.createNoteSuccess, closingNoteActions.createNoteFailure),
        // A comment saved meanwhile is not the dialog's answer.
        filter((action) => !('note' in action) || action.note.kind === 'TODO'),
        map((action) => action.type === closingNoteActions.createNoteSuccess.type),
    );

    // ── Laden ─────────────────────────────────────────────────────────────────────────────────

    public load(projectId: string, kind: NoteKindWire): void {
        this.store.dispatch(closingNoteActions.loadNotes({ projectId, kind }));
    }

    /** Der Konto-Bereich listet NUR Kommentare (ADR-0019 E10) — die Art ist hier deshalb fest. */
    public loadForAccount(projectId: string, accountId: string): void {
        this.store.dispatch(closingNoteActions.loadNotes({ projectId, kind: 'COMMENT', accountId }));
    }

    /** The Pendenzen of ONE account, for the account page's Pendenzen area. */
    public loadTodosForAccount(projectId: string, accountId: string): void {
        this.store.dispatch(closingNoteActions.loadNotes({ projectId, kind: 'TODO', accountId }));
    }

    /**
     * REV-21369 — die Notizen EINES Belegs (ADR-0021 E6), BEIDE Arten.
     *
     * ⚠️ Beide, weil eine Pendenz mit Markierung ihre Markierung genauso zeigt wie ein Kommentar
     * (Code-Review Runde 1, R2, Variante a). GELISTET werden am Beleg weiterhin nur Kommentare — die
     * Pendenz steht im Register „Pendenzen" (ADR-0019 E10); die Seiten filtern das.
     */
    public loadForDocument(projectId: string, documentId: string): void {
        this.store.dispatch(closingNoteActions.loadNotes({ projectId, kind: 'COMMENT', documentId }));
        this.store.dispatch(closingNoteActions.loadNotes({ projectId, kind: 'TODO', documentId }));
    }

    public loadOne(projectId: string, kind: NoteKindWire, noteId: string): void {
        this.store.dispatch(closingNoteActions.loadNote({ projectId, kind, noteId }));
    }

    // ── Schreiben ─────────────────────────────────────────────────────────────────────────────

    public create(projectId: string, request: ICreateNoteRequest): void {
        this.store.dispatch(closingNoteActions.createNote({ projectId, request }));
    }

    public update(projectId: string, noteId: string, request: IUpdateNoteRequest): void {
        this.store.dispatch(closingNoteActions.updateNote({ projectId, noteId, request }));
    }

    public remove(projectId: string, noteId: string): void {
        this.store.dispatch(closingNoteActions.deleteNote({ projectId, noteId }));
    }

    public setDone(projectId: string, noteId: string, done: boolean): void {
        this.store.dispatch(closingNoteActions.setNoteDone({ projectId, noteId, done }));
    }

    public reply(projectId: string, noteId: string, text: string, mentionUserIds: string[]): void {
        this.store.dispatch(closingNoteActions.replyToNote({ projectId, noteId, text, mentionUserIds }));
    }

    // ── Ansichtszustand ───────────────────────────────────────────────────────────────────────

    public setCommentFilter(filter: 'all' | 'internal' | 'closingMeeting'): void {
        this.store.dispatch(closingNoteActions.setCommentFilter({ filter }));
    }

    public setTodoStatus(status: 'open' | 'done'): void {
        this.store.dispatch(closingNoteActions.setTodoStatus({ status }));
    }

    public setTodoAudience(audience: 'all' | 'todo' | 'reviewer' | 'client'): void {
        this.store.dispatch(closingNoteActions.setTodoAudience({ audience }));
    }

    /** `key: null` is the third click — back to the default order. */
    public setSort(key: string | null, direction: 'asc' | 'desc'): void {
        this.store.dispatch(closingNoteActions.setSort({ key, direction }));
    }

    public removeFilter(key: string): void {
        this.store.dispatch(closingNoteActions.removeFilter({ key }));
    }

    public resetFilters(): void {
        this.store.dispatch(closingNoteActions.resetFilters());
    }

    public setSearchTerm(term: string): void {
        this.store.dispatch(closingNoteActions.setSearchTerm({ term }));
    }

    public toggleThread(noteId: string): void {
        this.store.dispatch(closingNoteActions.toggleThread({ noteId }));
    }

    public startEdit(noteId: string | null): void {
        this.store.dispatch(closingNoteActions.startEdit({ noteId }));
    }

    public openComposer(kind: NoteKindWire | null): void {
        this.store.dispatch(closingNoteActions.openComposer({ kind }));
    }

    public clearRecentlyChanged(): void {
        this.store.dispatch(closingNoteActions.clearRecentlyChanged());
    }
}
